import type { EnrolmentActionTarget } from './enrolmentActionTarget';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { getAdminNoteCategoryOptions } from '../Notes';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AdminNoteCategory } from '@duke-hq/svc-api-db';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  enrolment: Pick<EnrolmentActionTarget, 'id'>;
};

function WithdrawEnrolmentModal({ enrolment, isOpen, onClose }: Props) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const withdrawEnrolment = trpcAdmin.enrolment.withdrawCourseEnrolment.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: `Course enrolment has been withdrawn`,
      });

      await utils.enrolment.byIdWithResourceStatus.invalidate({
        enrolmentId: enrolment.id,
      });
      await utils.enrolment.list.invalidate(undefined, { refetchType: 'active' });
      resetFieldsAndClose();
    },
    async onError(error) {
      toast({
        status: 'error',
        title: `An error occurred while withdrawing the course enrolment`,
        description: error?.message,
      });

      await utils.enrolment.byIdWithResourceStatus.invalidate({
        enrolmentId: enrolment.id,
      });
      await utils.enrolment.list.invalidate(undefined, { refetchType: 'active' });
    },
  });
  const methods = useForm<{
    category: AdminNoteCategory;
    text: string;
    notifyStudentAndConsultant: boolean;
  }>({
    defaultValues: {
      notifyStudentAndConsultant: true,
      category: undefined,
      text: '',
    },
  });
  const {
    clearErrors,
    reset,
    formState: { errors },
    watch,
  } = methods;
  const withdrawEnrolmentTypeOptions = getAdminNoteCategoryOptions('WITHDRAW_ENROLMENT');
  const resetFieldsAndClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  const selectedCategory = watch('category');
  const notifyStudentAndConsultant = watch('notifyStudentAndConsultant');

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={() => {
        resetFieldsAndClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        padding={4}
        minH={'300px'}
        as={'form'}
        onSubmit={methods.handleSubmit((data) => {
          withdrawEnrolment.mutate({
            enrolmentId: enrolment.id,
            category: data.category,
            noteText: data.text,
            notifyStudentAndConsultant: data.notifyStudentAndConsultant,
          });
        })}
      >
        <ModalHeader>
          Withdraw enrolment
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={6} h="full">
          <FormProvider {...methods}>
            <VStack gap="8px" w="full" py="8px">
              <HStack w="full" justify="space-between" align="start" gap="10px">
                <FormControl id="category" isInvalid={!!errors.category} isRequired>
                  <FormLabel htmlFor="category" fontWeight="medium" fontSize="sm">
                    <Trans>Reason</Trans>
                  </FormLabel>
                  <Controller
                    name="category"
                    control={methods.control}
                    render={({ field }) => (
                      <Select
                        inputId="category"
                        name="category"
                        placeholder={t`Please select`}
                        options={withdrawEnrolmentTypeOptions}
                        onChange={(val) => {
                          field.onChange(AdminNoteCategory[String(val?.value)]);
                          clearErrors();
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
            <VStack>
              <FormControl isInvalid={!!errors.text?.message}>
                <FormLabel
                  color="fg.emphasis"
                  fontWeight="medium"
                  fontSize="sm"
                  mb={1}
                  htmlFor="text"
                >
                  <Trans>Notes</Trans>
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                </FormLabel>
                <Controller
                  name="text"
                  rules={{
                    required: 'Notes required',
                  }}
                  control={methods.control}
                  render={({ field }) => (
                    <Textarea id="text" height="auto" rows={5} mb={2} {...field} />
                  )}
                />
                <FormErrorMessage mb="2">{errors.text?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
            <VStack mb="1">
              <FormControl>
                <Controller
                  name="notifyStudentAndConsultant"
                  control={methods.control}
                  render={({ field }) => (
                    <Checkbox
                      name="notifyStudentAndConsultant"
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                      isChecked={notifyStudentAndConsultant}
                    >
                      <Text textStyle="sm" color="fg.muted">
                        <Trans>Notify student and consultant</Trans>
                      </Text>
                    </Checkbox>
                  )}
                />
              </FormControl>
            </VStack>
          </FormProvider>
          {selectedCategory ? (
            <Alert status="warning">
              <HStack>
                <AlertIcon />
              </HStack>
              <AlertDescription>
                <Text textStyle="sm" color="fg.muted" align="left">
                  <Trans>
                    Enrolment will be withdrawn immediately and student wil no longer have access to
                    future course material
                  </Trans>
                </Text>
                <Text textStyle="sm" color="fg.muted" align="left" mt={1}>
                  <Trans>Any active work placement on this enrolment will also be cancelled.</Trans>
                </Text>
              </AlertDescription>
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="button" variant="ghost" colorScheme="black" onClick={resetFieldsAndClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isDisabled={!selectedCategory}
            isLoading={withdrawEnrolment.isPending}
          >
            Withdraw this enrolment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default WithdrawEnrolmentModal;
